import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { showSnackbar } from 'components/shared/index';
import { difference } from 'ramda';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { createGrant, deleteGrant } from 'RestAPIs/admin';
import { useGetOptions } from '../../../../../../../../../hooks/useGetOptions';
import {
  useGetGrants,
  useGetGrantsCatalog
} from '../../../../../hooks/queries';
import PermissionFormFields from './PermissionFormFields';
import schema from './schema';

import type { Grant } from 'components/admin-portal/Grants/GrantsModules/types';
import type { FC } from 'react';
import type { TabProps } from '../../types';

const Permissions: FC<TabProps> = ({
  setIsNavigationDisabled,
  rows,
  toggleInnerLayout,
  currentViewData,
  setCurrentViewData,
  setIsDirty
}) => {
  const { t } = useTranslation('common');
  const [initialGrantIds, setInitialGrantIds] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const { data: grantsCatalog = [], isError } = useGetGrantsCatalog({
    queryParams: {
      type: 'LOGIC'
    }
  });

  const grantsCatalogOptions = useGetOptions(
    grantsCatalog,
    isError,
    'grantCatalogId',
    'name',
    'grants.'
  );

  const { data } = useGetGrants({
    queryParams: {
      groupId: currentViewData.groupId,
      type: 'LOGIC'
    }
  });

  const grants = data as Grant[];

  const methods = useForm({
    defaultValues: schema.cast({
      grantCatalogIds: []
    }),
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    const grantIds = grants
      ? [...new Set(grants.map((elem) => elem.grantCatalogId))]
      : [];

    if (grantIds.length > 0) {
      setInitialGrantIds(grantIds);
      methods.reset({
        grantCatalogIds: grants.map(({ grantCatalogId }) => grantCatalogId)
      });
    }
  }, [grants, currentViewData, methods]);

  const handleSubmit = async (dataToSave) => {
    const idsToRemove = difference(
      initialGrantIds,
      dataToSave.grantCatalogIds
    ).map(
      (grantCatalogId) =>
        grants.find((grant) => grant.grantCatalogId === grantCatalogId)?.grantId
    ) as number[];
    const idsToCreate = difference(
      dataToSave.grantCatalogIds,
      initialGrantIds
    ) as number[];

    return Promise.all([
      Promise.all(idsToRemove.map((grantId) => deleteGrant(grantId))),
      Promise.all(
        idsToCreate.map((grantId) => {
          const findGrantFromCatalog = grantsCatalog.find(
            (catalogGrant) => catalogGrant.grantCatalogId === grantId
          );

          createGrant({
            ...findGrantFromCatalog,
            groupId: currentViewData.groupId
          });
        })
      )
    ])
      .then(() => {
        showSnackbar({
          severity: 'success',
          message: t('changesSaved')
        });
      })
      .finally(() => {
        queryClient.invalidateQueries('grants');
      });
  };

  return (
    <PermissionFormFields
      setIsNavigationDisabled={setIsNavigationDisabled}
      toggleInnerLayout={toggleInnerLayout}
      rowData={currentViewData}
      rows={rows}
      methods={methods}
      onSubmit={handleSubmit}
      currentViewData={currentViewData}
      setCurrentViewData={setCurrentViewData}
      grants={grants}
      grantsCatalog={grantsCatalogOptions}
      setIsDirty={setIsDirty}
    />
  );
};

export default Permissions;
